import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import PageLayout from 'cxfcomponents/LayoutContainers/PageLayout';
import SoftBox from 'components/SoftBox';
import Flow from './components/Flow';
import Titlebar from './components/Titlebar';
import Toolbox from './components/Toolbox';
import Box from '@mui/material/Box';
import { autoUpdateCurrentFlow, setCanvasFlowInstance, fetchFlows, setSavedFlow } from 'redux/actions/flowDesigner/flowsActionCreator';
import { fetchToolboxData } from 'redux/actions/flowDesigner/componentGroupsActionCreator';
import { TabContextApi } from './context';
import { fetchFlowById as fetchCurrentFlowById } from 'api/FlowDesignerApis';
import { tryCatchWrapper } from 'helpers/common';
import { EDGE_TYPES } from 'constants/flowDesigner';
import { adjustTriggerCredentials } from './helpers/common';
import { DesignerHomeTab } from './components/Home';
import { useSharedConnectors } from 'hooks/useSharedConnectors';
import { useOwnedConnectors } from 'hooks/useOwnedConnectors';
import store from 'redux/store';
import { FlexLayoutContextProvider } from '../../context/AppProvider/FlexLayoutContext';
import { useCredentials } from '../../hooks/useCredentials';
import { DesignerSockets } from './components/Sockets';
import { resetExecutionReports } from 'redux/actions/flowDesigner/flowExecutionActionCreator';
import { useResourceListener } from 'hooks/useResourceListener';
import { PORTAL_HEADER_HEIGHT } from 'cxfcomponents/portalShell';

function FlowDesigner() {
	const { currentSavedFlow, canvasFlowInstance, copiedNode } = useSelector((store) => store?.designer?.flows);
	const { flowTriggers } = useSelector((store) => store?.designer?.triggers);
	const dispatch = useDispatch();
	const [savedDateTime, setSavedDateTime] = useState('');
	const { user } = useAuth0();
	const { id } = useParams();
	const titlebarRef = useRef();
	const currentTriggers = useRef(flowTriggers);
	const currentCopiedNode = useRef(copiedNode);
	currentTriggers.current = flowTriggers;
	currentCopiedNode.current = copiedNode;
	const { components } = useSelector((store) => store?.designer?.cxfComponents);
	useSharedConnectors();
	const { handleFetchOwnedConnectors } = useOwnedConnectors();
	useResourceListener('connectorsSharingUpdate', handleFetchOwnedConnectors);
	useResourceListener('flowsSharingUpdate', handleFetchDesignerFlows);
	const [toolboxWidth, setToolboxWidth] = useState(0);
	const { credentials } = useCredentials();
	const [isLoadingFlow, setIsLoadingFlow] = useState(false);
	const isMounted = useRef(false);
	const flowRef = useRef();
	const saveTimeoutRef = useRef(null);

	const autoSaveFlow = (reactFlowInstance, tabTitle, flowNodes, clearCopiedNode) => {
		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current);
		}
		saveTimeoutRef.current = setTimeout(() => {
			const tenant_id = user?.stripeId;

			const flowName = tabTitle || currentSavedFlow?.name;

			try {
				if (reactFlowInstance) {
					dispatch(setCanvasFlowInstance(reactFlowInstance.toObject()));
				}

				const input = tryCatchWrapper(getFlowInput)(reactFlowInstance, flowName, flowNodes, clearCopiedNode);

				if (currentSavedFlow) {
					dispatch(autoUpdateCurrentFlow(tenant_id, currentSavedFlow.id, input, user.cxfUserId));
					const date = new Date().toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
					setSavedDateTime(date);
				}
			} catch (err) {
				console.log(err);
			}
		}, 1000);
	};

	const getFlowInput = (reactFlowInstance, tabTitle, flowNodes, clearCopiedNode) => {
		const getFieldOrEmpty = (fieldName) => {
			return currentSavedFlow ? currentSavedFlow[fieldName] : '';
		};

		let steps = reactFlowInstance ? reactFlowInstance.toObject() : canvasFlowInstance;

		if (flowNodes && steps?.nodes) {
			steps.nodes = flowNodes;
		}

		//clear the base64 urls
		steps?.nodes.forEach((s) => {
			if (s.data.logo && s.data.logo.indexOf('http') === -1) {
				s.data.logo = '';
			}
		});

		const nodes = steps?.nodes ?? [];

		const connectors = nodes.filter((node) => node.type === 'custom');
		const connectorsMap = {};

		connectors.forEach((connector) => {
			if (connector.data?.componentId && !connectorsMap[connector.data.componentId]) {
				const foundConnector = components.find((component) => component.id === connector.data.componentId);

				if (foundConnector) {
					connectorsMap[foundConnector.id] = foundConnector;
				}
			}
		});

		const flowCredentialsId = nodes
			.filter((node) => node.type !== 'static')
			.map((node) => node.data?.flowElement?.data?.credential?.value)
			.filter((id) => !!id);
		const uniqueFlowCredentialsId = Array.from(new Set(flowCredentialsId));

		const filteredCredentials = credentials.filter((credential) => uniqueFlowCredentialsId.includes(credential.id));

		const contextCopy = { ...store.getState().designer.editorContext.context };

		const { adjustedTriggers, adjustedContext } = adjustTriggerCredentials(currentTriggers?.current, contextCopy);

		steps.context = adjustedContext;
		steps.triggers = adjustedTriggers;

		if (steps.context?.flow_configuration) {
			delete steps.context.flow_configuration;
		}

		if (steps.context?.shared_flow_configurations) {
			delete steps.context.shared_flow_configurations;
		}

		// purges all floating edge, we only want it on client
		steps.edges = steps.edges.filter((edge) => edge.type !== EDGE_TYPES.FLOATING);

		// store the copied node in the saved flow to be able to paste it later even if the browser reloads
		if (clearCopiedNode) {
			steps.copiedNode = null;
		} else {
			steps.copiedNode = currentCopiedNode.current;
		}

		steps = JSON.stringify(steps);

		return {
			created_by: currentSavedFlow ? currentSavedFlow.created_by : user?.name,
			created_by_id: currentSavedFlow ? currentSavedFlow.created_by_id : user?.cxfUserId,
			description: currentSavedFlow?.description ?? '',
			group_id: getFieldOrEmpty('group_id'),
			group_name: getFieldOrEmpty('group_name'),
			name: tabTitle,
			notes: currentSavedFlow?.notes ?? '',
			platform_name: getFieldOrEmpty('platform_name'),
			platform_type: getFieldOrEmpty('platform_type'),
			shared_link: getFieldOrEmpty('shared_link'),
			shared_status: currentSavedFlow?.shared_status,
			steps: `${steps}`,
			trashed: currentSavedFlow ? currentSavedFlow.trashed : false,
			flow_settings: currentSavedFlow?.flow_settings ? (typeof currentSavedFlow.flow_settings === 'string' ? currentSavedFlow.flow_settings : JSON.stringify(currentSavedFlow?.flow_settings)) : null,
			connectors: Object.values(connectorsMap).map((connector) => JSON.stringify(connector)),
			credentials: filteredCredentials.map((credential) => JSON.stringify(credential)),
			current_version: currentSavedFlow ? currentSavedFlow.current_version : '1.0'
		};
	};

	const toggleUndo = useCallback(
		(enableUndo) => {
			titlebarRef?.current?.setUndo(enableUndo);
		},
		[titlebarRef]
	);

	const toggleRedo = useCallback(
		(enableRedo) => {
			titlebarRef?.current?.setRedo(enableRedo);
		},
		[titlebarRef]
	);

	const handleUndo = () => {
		flowRef.current?.undo();
	}

	const handleRedo = () => {
		flowRef.current?.redo();
	}

	function handleFetchDesignerFlows() {
		dispatch(fetchFlows(user?.stripeId));
	}

	useEffect(() => {
		document.title = 'CXFabric - Designer';

		const loadFlow = async (flowId) => {
			try {
				setIsLoadingFlow(true);
				const flow = await fetchCurrentFlowById(user?.stripeId, flowId);

				if (!flow) return;

				dispatch(setSavedFlow(flow));
				setTimeout(() => {
					setIsLoadingFlow(false);
				}, 300);
			} catch (err) {
				console.log(err);
			}
		};

		if (id) {
			loadFlow(id);
		}

		handleFetchDesignerFlows();
		dispatch(fetchToolboxData(user?.stripeId));
	}, []);

	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}

		if (!currentSavedFlow?.id) {
			dispatch(resetExecutionReports());
		}
	}, [currentSavedFlow?.id]);

	return (
		<PageLayout>
			<TabContextApi.Provider value={{ autoSaveFlow, toggleRedo, toggleUndo }}>
				<FlexLayoutContextProvider>
					<DesignerSockets getFlowInput={getFlowInput} />
					<SoftBox className="designer-shell" height="100dvh" display="flex" flexDirection="row" sx={{ overflow: 'hidden' }}>
						<Toolbox onWidthChange={setToolboxWidth} />
						<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
							<SoftBox className="designer-header" sx={{ height: PORTAL_HEADER_HEIGHT, flexShrink: 0, position: 'relative', zIndex: 10 }}>
								<Titlebar ref={titlebarRef} savedDateTime={savedDateTime} handleUndo={handleUndo} handleRedo={handleRedo} />
							</SoftBox>
							<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', transition: 'margin-left 0.3s ease', marginLeft: `${toolboxWidth}px` }}>
								{isLoadingFlow ? <></> : currentSavedFlow ? <Flow ref={flowRef} /> : <DesignerHomeTab />}
							</Box>
						</Box>
					</SoftBox>
				</FlexLayoutContextProvider>
			</TabContextApi.Provider>
		</PageLayout>
	);
}

export default FlowDesigner;
